import { useEffect, useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { User, Table, Check, ShieldStar, FloppyDisk, Lock, UserPlus, Trash } from "@phosphor-icons/react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { fmt } from "../lib/format";

function ChangePasswordCard() {
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 6) return toast.error("Kata sandi baru minimal 6 karakter");
    if (next !== confirm) return toast.error("Konfirmasi kata sandi tidak cocok");
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: next });
      toast.success("Kata sandi berhasil diubah");
      setCur(""); setNext(""); setConfirm("");
    } catch (e2) {
      toast.error(formatApiError(e2.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-2xl bg-card p-5 shadow-sm ring-1 ring-black/[0.03] mb-6" data-testid="change-password-card">
      <div className="flex items-center gap-2 mb-4">
        <span className="w-9 h-9 rounded-xl bg-accent flex items-center justify-center"><Lock size={18} weight="fill" className="text-primary" /></span>
        <div>
          <h3 className="font-display font-bold text-base">Ganti Kata Sandi</h3>
          <p className="text-xs text-muted-foreground">Perbarui kata sandi akun Anda</p>
        </div>
      </div>
      <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
        <div>
          <label className="text-xs font-semibold text-muted-foreground">Kata sandi saat ini</label>
          <Input type="password" value={cur} onChange={(e) => setCur(e.target.value)} required
            className="mt-1 rounded-xl" data-testid="cp-current" />
        </div>
        <div>
          <label className="text-xs font-semibold text-muted-foreground">Kata sandi baru</label>
          <Input type="password" value={next} onChange={(e) => setNext(e.target.value)} required
            className="mt-1 rounded-xl" data-testid="cp-new" />
        </div>
        <div>
          <label className="text-xs font-semibold text-muted-foreground">Konfirmasi</label>
          <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required
            className="mt-1 rounded-xl" data-testid="cp-confirm" />
        </div>
        <div className="sm:col-span-3">
          <Button type="submit" disabled={saving} className="rounded-xl gap-2" data-testid="cp-submit">
            <FloppyDisk size={16} /> {saving ? "Menyimpan…" : "Simpan Kata Sandi"}
          </Button>
        </div>
      </form>
    </div>
  );
}

function CreateUserCard({ onCreated }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("user");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) return toast.error("Kata sandi minimal 6 karakter");
    setSaving(true);
    try {
      await api.post("/users", { name, email, password, role });
      toast.success(`${role === "admin" ? "Admin" : "Pengguna"} "${name || email}" dibuat`);
      setName(""); setEmail(""); setPassword(""); setRole("user");
      onCreated?.();
    } catch (e2) {
      toast.error(formatApiError(e2.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-2xl bg-card p-5 shadow-sm ring-1 ring-black/[0.03] mb-4" data-testid="create-user-card">
      <div className="flex items-center gap-2 mb-4">
        <span className="w-9 h-9 rounded-xl bg-accent flex items-center justify-center"><UserPlus size={18} weight="fill" className="text-primary" /></span>
        <div>
          <h3 className="font-display font-bold text-base">Buat Pengguna / Admin Baru</h3>
          <p className="text-xs text-muted-foreground">Tambahkan akun dan tentukan perannya</p>
        </div>
      </div>
      <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 items-end">
        <div>
          <label className="text-xs font-semibold text-muted-foreground">Nama</label>
          <Input value={name} onChange={(e) => setName(e.target.value)} required className="mt-1 rounded-xl" data-testid="nu-name" />
        </div>
        <div>
          <label className="text-xs font-semibold text-muted-foreground">Email</label>
          <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1 rounded-xl" data-testid="nu-email" />
        </div>
        <div>
          <label className="text-xs font-semibold text-muted-foreground">Kata sandi</label>
          <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="mt-1 rounded-xl" data-testid="nu-password" />
        </div>
        <div>
          <label className="text-xs font-semibold text-muted-foreground">Peran</label>
          <Select value={role} onValueChange={setRole}>
            <SelectTrigger className="mt-1 rounded-xl" data-testid="nu-role"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="user">Pengguna</SelectItem>
              <SelectItem value="admin">Admin</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="sm:col-span-2 lg:col-span-4">
          <Button type="submit" disabled={saving} className="rounded-xl gap-2" data-testid="nu-submit">
            <UserPlus size={16} /> {saving ? "Membuat…" : "Buat Akun"}
          </Button>
        </div>
      </form>
    </div>
  );
}

export default function Settings() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [users, setUsers] = useState([]);
  const [sources, setSources] = useState([]);
  const [selected, setSelected] = useState(null);
  const [allowed, setAllowed] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const loadData = async () => {
    try {
      const [u, s] = await Promise.all([api.get("/users"), api.get("/datasources")]);
      setUsers(u.data);
      setSources(s.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) loadData();
    else setLoading(false);
  }, [isAdmin]);

  const pickUser = async (u) => {
    setSelected(u);
    try {
      const { data } = await api.get(`/users/${u.id}/dashboards`);
      setAllowed(data.datasource_ids || []);
    } catch {
      setAllowed([]);
    }
  };

  const toggle = (id) => setAllowed((a) => (a.includes(id) ? a.filter((x) => x !== id) : [...a, id]));

  const save = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await api.put(`/users/${selected.id}/dashboards`, { datasource_ids: allowed });
      toast.success(`Akses untuk ${selected.name || selected.email} disimpan`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const removeUser = async (u, ev) => {
    ev.stopPropagation();
    if (!window.confirm(`Hapus pengguna ${u.name || u.email}?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("Pengguna dihapus");
      if (selected?.id === u.id) setSelected(null);
      loadData();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const [resetPw, setResetPw] = useState("");
  const [resetting, setResetting] = useState(false);
  useEffect(() => { setResetPw(""); }, [selected]);

  const doReset = async () => {
    if (resetPw.length < 6) return toast.error("Kata sandi baru minimal 6 karakter");
    setResetting(true);
    try {
      await api.post(`/users/${selected.id}/reset-password`, { new_password: resetPw });
      toast.success(`Kata sandi ${selected.name || selected.email} direset`);
      setResetPw("");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setResetting(false);
    }
  };

  return (
    <div className="p-4 lg:p-6 max-w-5xl">
      <div className="mb-5">
        <p className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground font-semibold">Pengaturan</p>
        <h1 className="font-display font-extrabold text-2xl sm:text-3xl tracking-tight mt-1">Pengaturan Akun</h1>
        <p className="text-muted-foreground text-sm mt-1.5">Kelola kata sandi{isAdmin && ", pengguna, dan akses sumber data"}.</p>
      </div>

      {/* Semua user bisa ganti password */}
      <ChangePasswordCard />

      {!isAdmin ? null : loading ? (
        <div className="text-sm text-muted-foreground animate-pulse">Memuat…</div>
      ) : (
        <>
          <CreateUserCard onCreated={loadData} />

          <h2 className="font-display font-bold text-lg tracking-tight mb-1">Akses Sumber Data</h2>
          <p className="text-muted-foreground text-sm mb-3">Pilih pengguna, lalu tentukan sumber data yang boleh diakses.</p>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
            {/* User list */}
            <div className="lg:col-span-5 rounded-2xl bg-card p-3 shadow-sm ring-1 ring-black/[0.03]">
              <p className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground font-semibold px-2 py-2">Pengguna ({users.length})</p>
              <div className="space-y-1 max-h-[60vh] overflow-y-auto">
                {users.map((u) => {
                  const active = selected?.id === u.id;
                  return (
                    <div key={u.id}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors group ${active ? "bg-accent" : "hover:bg-secondary"}`}>
                      <button onClick={() => pickUser(u)} className="flex items-center gap-3 flex-1 min-w-0 text-left" data-testid={`user-item-${u.id}`}>
                        <span className="w-9 h-9 rounded-xl bg-secondary flex items-center justify-center shrink-0">
                          {u.role === "admin" ? <ShieldStar size={18} className="text-primary" weight="fill" /> : <User size={18} className="text-muted-foreground" />}
                        </span>
                        <span className="min-w-0">
                          <span className="block text-sm font-semibold truncate">{u.name || "Pengguna"}</span>
                          <span className="block text-xs text-muted-foreground truncate">{u.email}</span>
                        </span>
                        {u.role === "admin" && <span className="ml-auto text-[10px] uppercase tracking-wider text-primary font-bold shrink-0">admin</span>}
                      </button>
                      {u.id !== user.id && (
                        <button onClick={(ev) => removeUser(u, ev)} title="Hapus pengguna"
                          className="p-1.5 rounded-lg text-muted-foreground hover:text-destructive hover:bg-destructive/10 opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
                          data-testid={`delete-user-${u.id}`}>
                          <Trash size={16} />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Dashboard access */}
            <div className="lg:col-span-7 rounded-2xl bg-card p-4 shadow-sm ring-1 ring-black/[0.03]">
              {!selected ? (
                <div className="h-full min-h-[240px] flex items-center justify-center text-sm text-muted-foreground" data-testid="settings-empty">
                  Pilih pengguna di kiri untuk mengatur akses.
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
                    <div>
                      <h3 className="font-display font-bold text-base">{selected.name || selected.email}</h3>
                      <p className="text-xs text-muted-foreground">{allowed.length} dari {sources.length} sumber data dipilih</p>
                    </div>
                    <Button onClick={save} disabled={saving || selected.role === "admin"} className="rounded-xl gap-2 h-9" data-testid="save-access-button">
                      <FloppyDisk size={16} /> {saving ? "Menyimpan…" : "Simpan"}
                    </Button>
                  </div>

                  {selected.role === "admin" && (
                    <div className="mb-3 rounded-xl bg-accent/60 px-3 py-2 text-xs text-primary">
                      Admin otomatis punya akses ke semua sumber data.
                    </div>
                  )}

                  {/* Reset kata sandi oleh admin */}
                  <div className="mb-4 rounded-xl ring-1 ring-border p-3" data-testid="reset-password-box">
                    <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold mb-2 flex items-center gap-1.5">
                      <Lock size={13} weight="fill" className="text-primary" /> Reset Kata Sandi
                    </p>
                    <div className="flex gap-2">
                      <Input type="text" value={resetPw} onChange={(e) => setResetPw(e.target.value)}
                        placeholder="Kata sandi baru (min. 6)" className="rounded-xl h-9" data-testid="reset-pw-input" />
                      <Button onClick={doReset} disabled={resetting} variant="secondary" className="rounded-xl h-9 shrink-0" data-testid="reset-pw-submit">
                        {resetting ? "…" : "Reset"}
                      </Button>
                    </div>
                    <p className="text-[11px] text-muted-foreground mt-1.5">Beri kata sandi baru untuk pengguna yang lupa sandi.</p>
                  </div>

                  <div className="space-y-2 max-h-[52vh] overflow-y-auto">
                    {sources.length === 0 && <p className="text-sm text-muted-foreground">Belum ada sumber data.</p>}
                    {sources.map((s) => {
                      const on = allowed.includes(s.id);
                      return (
                        <motion.button key={s.id} onClick={() => toggle(s.id)} whileTap={{ scale: 0.99 }}
                          disabled={selected.role === "admin"}
                          className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl text-left ring-1 transition-colors disabled:opacity-50 ${on ? "ring-primary/40 bg-accent/40" : "ring-border hover:bg-secondary"}`}
                          data-testid={`access-toggle-${s.id}`}>
                          <span className={`w-5 h-5 rounded-md flex items-center justify-center shrink-0 ${on ? "bg-primary" : "ring-1 ring-border"}`}>
                            {on && <Check size={13} weight="bold" className="text-primary-foreground" />}
                          </span>
                          <Table size={16} className="text-muted-foreground shrink-0" />
                          <span className="min-w-0">
                            <span className="block text-sm font-medium truncate">{s.name}</span>
                            <span className="block text-xs text-muted-foreground">{fmt(s.row_count)} baris · {s.columns?.length || 0} kolom</span>
                          </span>
                        </motion.button>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
